/* eslint-disable react/no-unescaped-entities */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LmsShell, studentApi, setStudentSession } from "./portal/shell";

export default function StudentLogin() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [showResend, setShowResend] = useState(false);
  const [resent, setResent] = useState(false);

  // When the user changes the email after a reset-link send, clear the
  // "Reset link sent" state so they can request a link for a different
  // account (common failure mode: typed the wrong email first time).
  const onEmailChange = (v) => {
    setEmail(v);
    if (resent) setResent(false);
    if (err) setErr("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const data = await studentApi.login(email, pw);
      setStudentSession(data);
      nav("/dashboard", { replace: true });
    } catch (ex) {
      setErr(ex.message || "Login failed.");
      setShowResend(true);
    } finally { setBusy(false); }
  };

  const resendMagic = async () => {
    if (!email.trim()) {
      setErr("Enter the email you enrolled with, then click 'Forgot password?' again.");
      // Focus the email field so the user can act immediately.
      document.querySelector('[data-testid="student-login-email"]')?.focus();
      return;
    }
    setErr("");
    setBusy(true);
    try {
      await studentApi.requestMagicLink(email.trim());
      setResent(true);
    } catch (ex) {
      setErr(ex.message || "Could not send reset link.");
    } finally { setBusy(false); }
  };

  return (
    <LmsShell
      subtitle="Student login"
      bgImage="https://customer-assets.emergentagent.com/job_tbirdgeo-preview/artifacts/xr9zr7i9_97d99be6-04c2-46f3-a5d6-c21b5078cb90.png"
    >
      <form className="tbg-lms-card" onSubmit={submit} data-testid="student-login-form">
        <div className="h-sub">Advanced RPAS · Sign in</div>
        <h2>Welcome back</h2>
        <p>Log in with the email you used at checkout.</p>

        <label className="tbg-lms-lbl">Email<span className="req">*</span></label>
        <input
          type="email" required autoFocus value={email}
          onChange={(e) => onEmailChange(e.target.value)}
          className="tbg-lms-input" placeholder="you@example.com"
          data-testid="student-login-email"
        />
        <label className="tbg-lms-lbl">Password<span className="req">*</span></label>
        <input
          type="password" required value={pw}
          onChange={(e) => setPw(e.target.value)}
          className="tbg-lms-input" placeholder="Your password"
          data-testid="student-login-pw"
        />
        {/* Always-visible forgot-password link. One-click sends a magic
            link to the email already in the form (or focuses the email
            field if it's empty). Works for BOTH "I never set a password"
            and "I forgot my password" cases. */}
        <div style={{ textAlign: "right", marginTop: -6, marginBottom: 6 }}>
          <button
            type="button"
            onClick={resendMagic}
            disabled={busy || resent}
            data-testid="student-login-forgot"
            style={{
              background: "transparent", border: 0, padding: 0,
              color: "#3dc0af", font: "500 12px 'JetBrains Mono', monospace",
              cursor: busy || resent ? "default" : "pointer",
              textDecoration: "underline", textUnderlineOffset: 3,
              opacity: busy || resent ? 0.5 : 1,
            }}
          >
            {resent ? "Reset link sent" : "Forgot password?"}
          </button>
        </div>
        {err && <div className="tbg-lms-err" data-testid="student-login-error">{err}</div>}
        {resent && (
          <div className="tbg-lms-ok" data-testid="student-login-resent">
            If that email is enrolled, a password-reset link is on the way.
            Check your inbox (and spam folder) — the link expires in 48 hours.
          </div>
        )}

        <button type="submit" className="tbg-lms-btn" disabled={busy} data-testid="student-login-submit">
          {busy ? "Signing in…" : "Sign in"}
        </button>

        {showResend && !resent && (
          <button type="button" onClick={resendMagic}
            className="tbg-lms-btn" style={{ marginTop: 10, background: "transparent", color: "#3dc0af", border: "1px solid #2a403c" }}
            data-testid="student-login-resend-magic">
            Never set a password? Email me a new link
          </button>
        )}

        {/* Secondary entry point for bulk / First Nations school students
            who log in with a School ID (no email required). Kept visually
            distinct from the primary email sign-in so neither path is
            mistaken for the other. */}
        <div
          style={{
            marginTop: 18, paddingTop: 14, borderTop: "1px solid #1c2a27",
            display: "flex", justifyContent: "space-between", alignItems: "center",
            gap: 10, flexWrap: "wrap",
          }}
        >
          <div style={{ color: "#7b8b87", font: "500 12px 'JetBrains Mono', monospace", letterSpacing: "0.04em" }}>
            Part of a school or class?
          </div>
          <Link
            to="/school-login"
            data-testid="student-login-school-link"
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "6px 14px", borderRadius: 999,
              border: "1px solid rgba(240,190,60,0.5)",
              color: "#f0be3c", background: "rgba(240,190,60,0.06)",
              font: "600 11px 'JetBrains Mono', monospace",
              letterSpacing: "0.14em", textTransform: "uppercase",
              textDecoration: "none",
            }}
          >
            School login →
          </Link>
        </div>

        <div className="tbg-lms-foot">
          <Link to="/groundschool">← Course info</Link>
          <Link to="/verify">Verify a certificate</Link>
        </div>
      </form>
    </LmsShell>
  );
}
